import { Alert, Backdrop, Box, Button, CircularProgress, Divider, Grid, Snackbar, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { getCurrencySymbol, monedasPresentes } from '../../../utils/Factura';
import FiltrosInformes, { IFiltrosInformes } from './componentes/FiltrosInformes';
import KPIsFacturacion from './componentes/KPIsFacturacion';
import GraficoIngresosTiempo from './componentes/GraficoIngresosTiempo';
import GraficoEstadoCobros from './componentes/GraficoEstadoCobros';
import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import FacturacionService from '../../../services/FacturacionService';
import ConfigUtils from '../../../utils/Config';
import { useNavigate } from 'react-router-dom';
import IAlert from '../../../types/Alert';
import DownloadIcon from "@mui/icons-material/Download";
import LogService from '../../../services/LogService';
import * as constantes from "../../../common/Constantes";
import ILog from '../../../types/Log';
import TablaPorPaciente from './componentes/TablaPorPaciente';
import { GraficosPorCategoria } from './componentes/GraficosPorCategoria';
import { getEntidadesConsulta, slugifyEntidadNombre } from '../common/SelectorEntidadConsulta';
import { isAxiosError } from 'axios';
import { esFechaValida } from '../../../utils/FechaSelector';

// Las descargas piden la respuesta como Blob: un error del servidor ({ message }) llega también como Blob y hay que
// leerlo para mostrar su mensaje ("No hay facturas en el periodo seleccionado", año natural...). Solo los 4xx: un 500
// devuelve el error interno, que no es un mensaje para el usuario.
const mensajeErrorBlob = async (e: unknown): Promise<string | undefined> => {
  if (!isAxiosError(e) || !e.response || e.response.status >= 500) return undefined;
  const data: unknown = e.response.data;
  if (!(data instanceof Blob)) return undefined;
  try {
    const json: unknown = JSON.parse(await data.text());
    return typeof json === 'object' && json !== null && 'message' in json && typeof json.message === 'string'
      ? json.message
      : undefined;
  } catch {
    return undefined;
  }
};

export interface FacturaInforme {
  numero: string;
  tipo: 'cita' | 'clase' | 'bono' | 'agrupada';
  total: number;
  estado: 'Borrador' | 'Emitida' | 'Anulada';
  estadoPago: 'Pendiente' | 'Pagada';
  paciente: string;
  servicio: string;
  empleado: string;
  fechaFactura: string;
  metodoPago?: string;
  fechaPago?: string;
  moneda?: string;
  entityId?: string | null;
  tipoRectificacion?: string | null;
}

// Una factura "cuenta" en los recuentos (nº de facturas, ratio de emisión...) salvo la rectificativa por anulación
// y la original que anula: aportan sus importes (se compensan) pero no son dos facturas más.
export const cuentaComoFactura = (f: FacturaInforme): boolean =>
  f.tipoRectificacion !== "anulacion" && f.estado !== "Anulada";

export type PresetFechas = "Hoy" | "Este mes" | "3 meses" | "1 año" | "Personalizado";

// ToggleButtonGroup no admite `null`/`undefined` como value: la entidad principal (entidadId:
// null) y "todas las entidades" (undefined) se representan aquí y se traducen de vuelta en
// onChange.
const PRINCIPAL_SENTINEL = "__principal__";
const TODAS_SENTINEL = "__todas__";

const Informes = () => {
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [filtros, setFiltros] = useState<IFiltrosInformes>(() => {
    const hoy = moment();

    return {
      fechaInicio: hoy.startOf('month').toDate(),
      fechaFin: hoy.endOf('month').toDate(),
    };
  });

  const [facturas, setFacturas] = useState<FacturaInforme[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  const [presetFechas, setPresetFechas] = useState<PresetFechas>("Este mes");

  const userId = ConfigUtils.getUserId();
  const empleados = ConfigUtils.getEmpleados();
  const empleadosMap = useMemo(() => {
    return new Map<number, string>(
      empleados.map(e => [
        e.internalID,
        `${e.firstName} ${e.lastName}`,
      ])
    );
  }, [empleados]);

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const response = await FacturacionService.getFacturacion(userId);
      // El servidor ya deja fuera las sustituidas y las anuladas sin negativa; las anuladas con negativa
      // cuentan (original cobrada en su periodo y negativa en el suyo).
      const facturasConEmpleado = (response.data || [])
        .map(f => {
          let nombre = "Sin especialista";      

          if (f.empleado !== null && f.empleado !== undefined) {
            nombre = empleadosMap.get(Number(f.empleado)) ?? "Sin especialista";
          } else if (f.tipo === "agrupada") {
            nombre = "Complementaria";
          } else if (f.tipo === "bono") {
            nombre = "Bonos";
          }     

          return {
            ...f,
            empleado: nombre,
          };
        });
      setFacturas(facturasConEmpleado);
    }  catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "Informes.Component.getFacturacion",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateFiltros = useCallback(
    (partial: Partial<IFiltrosInformes>) => {
      setFiltros(prev => ({ ...prev, ...partial }));
    },
    []
  );

  const resetFiltros = () => {
    setFiltros(prev => ({
      fechaInicio: prev.fechaInicio,
      fechaFin: prev.fechaFin,
      tipo: undefined,
      estado: undefined,
      estadoPago: undefined,
      servicios: [],
      empleado: undefined,
      paciente: undefined,
      metodoPago: undefined,
    }));
  };

  const handlePresetChange = (value: PresetFechas) => {
    setPresetFechas(value);
    let inicio: Date | null = null;
    let fin: Date | null = null;
    
    switch (value) {
      case "Hoy":
        inicio = moment().startOf("day").toDate();
        fin = moment().endOf("day").toDate();
        break;
      case "Este mes":
        inicio = moment().startOf("month").toDate();
        fin = moment().endOf("month").toDate();
        break;
      case "3 meses": {
        const mesActual = moment().month(); 
        const trimestre = Math.floor(mesActual / 3);
        inicio = moment().month(trimestre * 3).startOf("month").startOf("day").toDate();
        fin = moment().month(trimestre * 3 + 2).endOf("month").endOf("day").toDate();
        break;
      }
      case "1 año":
        inicio = moment().startOf("year").startOf("day").toDate();
        fin = moment().endOf("year").endOf("day").toDate();
        break;
      case "Personalizado":
        inicio = filtros.fechaInicio;
        fin = filtros.fechaFin;
        break;
    }
  
    setFiltros({
      fechaInicio: inicio!,
      fechaFin: fin!,
    });
  };

  const descargarResumen = async () => {
    try {
      setIsDownloading(true);

      if (!filtros.fechaInicio || !filtros.fechaFin) {
        setAlert({ open: true, severity: "warning", msg: "Indica las fechas de inicio y de fin" });
        return;
      }

      if (filtros.fechaInicio > filtros.fechaFin) {
        setAlert({
          open: true,
          severity: "warning",
          msg: "La fecha de inicio debe ser anterior a la de fin",
        });
        return;
      }

      // Una descarga = un año natural (mismo criterio que el servidor)
      if (moment(filtros.fechaInicio).year() !== moment(filtros.fechaFin).year()) {
        setAlert({
          open: true,
          severity: "warning",
          msg: "El periodo debe estar dentro del mismo año natural (del 1 de enero al 31 de diciembre)",
        });
        return;
      }

      const response = await FacturacionService.descargarResumen({
        userId: ConfigUtils.getUserId(),
        fechaInicio: moment(filtros.fechaInicio).format("YYYY-MM-DD"),
        fechaFin: moment(filtros.fechaFin).format("YYYY-MM-DD"),
        moneda: monedaSeleccionada || 'EUR',
        entidadId: entidadSeleccionada,
      });
    
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const format = (d: Date) =>
        `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;

      const nombreEntidad = entidadesConsulta.length > 1
        ? entidadesConsulta.find(ent => ent.entidadId === entidadSeleccionada)?.nombre
        : undefined;
      const slugEntidad = nombreEntidad ? `-${slugifyEntidadNombre(nombreEntidad)}` : "";

      const a = document.createElement("a");
      a.href = url;
      a.download = `resumen${slugEntidad}-${format(filtros.fechaInicio)}_${format(filtros.fechaFin)}.zip`;
      a.click();
    
      window.URL.revokeObjectURL(url);
      setAlert({ open: true, severity: "success", msg: "Resumen descargado correctamente" });
    } catch (e: unknown) {
      setAlert({
        open: true,
        severity: "error",
        msg: (await mensajeErrorBlob(e)) || "No se ha podido descargar el resumen",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const facturasFiltradas = facturas.filter(f => {
    const fecha = moment(f.fechaPago);
  
    const inicio = filtros.fechaInicio ? moment(filtros.fechaInicio).startOf('day') : null;
    const fin = filtros.fechaFin ? moment(filtros.fechaFin).endOf('day') : null;
  
    if (inicio && fecha.isBefore(inicio)) return false;
    if (fin && fecha.isAfter(fin)) return false;
  
    if (filtros.tipo && f.tipo !== filtros.tipo) return false;
    if (filtros.estado && f.estado !== filtros.estado) return false;
    if (filtros.estadoPago && f.estadoPago !== filtros.estadoPago) return false;
    if (filtros.servicios && Array.isArray(filtros.servicios) && filtros.servicios.length > 0) {
      if (!f.servicio || !filtros.servicios.includes(f.servicio)) return false;
    }
    if (filtros.empleado && f.empleado !== filtros.empleado) return false;
    if (filtros.paciente && f.paciente !== filtros.paciente) return false;
    if (filtros.metodoPago && f.metodoPago !== filtros.metodoPago) return false;

    return true;
  });

  const entidadesConsulta = useMemo(() => getEntidadesConsulta(), []);
  // null = principal · undefined = todas las entidades juntas (por defecto)
  const [entidadSeleccionada, setEntidadSeleccionada] = useState<string | null | undefined>(undefined);

  // La consulta en pantalla admite cualquier periodo; la descarga del resumen, solo un año natural (como el servidor).
  const cruzaDeAnio = !!filtros.fechaInicio && !!filtros.fechaFin
    && moment(filtros.fechaInicio).year() !== moment(filtros.fechaFin).year();
  // Fecha vacía o a medio escribir en el filtro: antes el botón decía que no era un año natural, que no era el problema
  const fechasNoValidas = !esFechaValida(filtros.fechaInicio) || !esFechaValida(filtros.fechaFin);
  const motivoSinDescarga = entidadesConsulta.length > 1 && entidadSeleccionada === undefined
    ? "Selecciona una entidad concreta para descargar el resumen"
    : fechasNoValidas
      ? "Indica unas fechas de inicio y de fin válidas para descargar el resumen"
      : cruzaDeAnio
      ? "La descarga debe estar dentro del mismo año natural (del 1 de enero al 31 de diciembre)"
      : "";

  const facturasFiltradasPorEntidad = useMemo(
    () => entidadSeleccionada === undefined
      ? facturasFiltradas
      : facturasFiltradas.filter(f => (f.entityId ?? null) === entidadSeleccionada),
    [facturasFiltradas, entidadSeleccionada]
  );

  const monedasDisponibles = useMemo(() => monedasPresentes(facturasFiltradasPorEntidad), [facturasFiltradasPorEntidad]);
  const [monedaSeleccionada, setMonedaSeleccionada] = useState<string | null>(null);

  useEffect(() => {
    if (monedasDisponibles.length === 0) return;
    if (monedaSeleccionada && monedasDisponibles.includes(monedaSeleccionada)) return;

    const monedaConfigurada = ConfigUtils.getUserCurrency();
    setMonedaSeleccionada(
      monedaConfigurada && monedasDisponibles.includes(monedaConfigurada)
        ? monedaConfigurada
        : monedasDisponibles[0]
    );
  }, [monedasDisponibles, monedaSeleccionada]);

  const facturasFiltradasPorMoneda = useMemo(
    // Antes de que el efecto fije la divisa por defecto, no se filtra nada — evita un
    // parpadeo de "sin datos" mientras monedaSeleccionada todavía vale null.
    () => monedaSeleccionada
      ? facturasFiltradasPorEntidad.filter(f => (f.moneda || 'EUR') === monedaSeleccionada)
      : facturasFiltradasPorEntidad,
    [facturasFiltradasPorEntidad, monedaSeleccionada]
  );

  const opcionesFiltros = useMemo(() => {
    const uniq = <T,>(arr: T[]) => Array.from(new Set(arr));
    const sort = (arr: string[]) =>
      arr.sort((a, b) => a.localeCompare(b, "es", { sensitivity: "base" }));

    return {
      tipos: uniq(facturas.map(f => f.tipo)),
      servicios: sort(uniq(facturas.map(f => f.servicio).filter(Boolean))),
      empleados: uniq(facturas.map(f => f.empleado).filter(Boolean)),
      pacientes: sort(uniq(facturas.map(f => f.paciente).filter(Boolean))),
      metodosPago: uniq(facturas.map(f => f.metodoPago).filter(Boolean)),
      estados: uniq(facturas.map(f => f.estado)),
      estadosPago: uniq(facturas.map(f => f.estadoPago)),
    };
  }, [facturas]);

  if (loading) return <CircularProgress />;
  
  return (
    <>
    <Stack spacing={2}>

      <FiltrosInformes
        filtros={filtros}
        onChange={updateFiltros}
        onReset={resetFiltros}
        opciones={opcionesFiltros}
        presetFechas={presetFechas}
        onPresetFechasChange={handlePresetChange}
      />

      {entidadesConsulta.length > 1 &&
        <ToggleButtonGroup
          value={entidadSeleccionada === undefined ? TODAS_SENTINEL : (entidadSeleccionada ?? PRINCIPAL_SENTINEL)}
          exclusive
          size="small"
          sx={{ flexWrap: "wrap", gap: 0.5, "& .MuiToggleButtonGroup-grouped": { borderRadius: "4px !important", borderLeft: "1px solid rgba(0, 0, 0, 0.12) !important" } }}
          onChange={(_, value) => {
            if (!value) return;
            setEntidadSeleccionada(value === TODAS_SENTINEL ? undefined : (value === PRINCIPAL_SENTINEL ? null : value));
          }}
        >
          <ToggleButton value={TODAS_SENTINEL}>Todas</ToggleButton>
          {entidadesConsulta.map(ent => (
            <ToggleButton key={ent.entidadId ?? PRINCIPAL_SENTINEL} value={ent.entidadId ?? PRINCIPAL_SENTINEL}>
              {ent.etiqueta}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      }

      <Divider sx={{ my: 1 }} />

      {facturasFiltradasPorEntidad.length === 0 ? (
      <Box mt={3} textAlign="center">
        <Typography variant="h6" color="text.secondary">
          No hay datos de facturación para los filtros seleccionados
        </Typography>
      </Box>
      ) : (
      <>
        {/* KPIs */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
        >
          <Typography variant="h5" fontWeight={600}>
            Tu resumen
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            width={{ xs: "100%", sm: "auto" }}
          >
            {monedasDisponibles.length > 1 &&
              <ToggleButtonGroup
                value={monedaSeleccionada}
                exclusive
                size="small"
                sx={{ flexWrap: "wrap", gap: 0.5, "& .MuiToggleButtonGroup-grouped": { borderRadius: "4px !important", borderLeft: "1px solid rgba(0, 0, 0, 0.12) !important" } }}
                onChange={(_, value) => value && setMonedaSeleccionada(value)}
              >
                {monedasDisponibles.map(moneda => (
                  <ToggleButton key={moneda} value={moneda} sx={{ minWidth: 32, px: 1, height: 30 }}>
                    {getCurrencySymbol(moneda)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            }

            <Tooltip title={motivoSinDescarga}>
              <Box component="span" sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="contained"
                  onClick={descargarResumen}
                  startIcon={<DownloadIcon />}
                  disabled={isDownloading || !!motivoSinDescarga}
                  size="small"
                  sx={{ width: { xs: "100%", sm: "auto" }, height: 30 }}
                >
                  Descargar resumen
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <KPIsFacturacion facturas={facturasFiltradasPorMoneda} />

        {/* Gráficos */}
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <GraficoEstadoCobros facturas={facturasFiltradasPorMoneda} />
            </Grid>
            <Grid item xs={12} md={8}>
              <GraficoIngresosTiempo facturas={facturasFiltradasPorMoneda} presetFechas={presetFechas} />
            </Grid>
          </Grid>
        </Box>

        {/* Detalle por categoría */}
        <GraficosPorCategoria facturas={facturasFiltradasPorMoneda} empleados={empleados} />

        {/* Tablas */}
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TablaPorPaciente facturas={facturasFiltradasPorMoneda} type="gasto" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TablaPorPaciente facturas={facturasFiltradasPorMoneda} type="deuda" />
            </Grid>
          </Grid>
        </Box>
      </>
      )}
    </Stack>

    <Backdrop
      open={isDownloading}
      sx={{ 
        color: "#fff", 
        zIndex: (theme) => theme.zIndex.drawer + 1, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 2, 
        textAlign: 'center',
        p: 2
      }}
    >
      <CircularProgress color="inherit" />
      <Typography variant="h6" component="div">
        Descargando resumen… Esto puede tardar unos segundos.
      </Typography>
    </Backdrop>

    <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
      <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
        {alert.msg}
      </Alert>
    </Snackbar>
    </>
  );
};

export default Informes;
